import type { CallCategory, SalesChecklistBehaviors } from "@expert/shared-types";

import { gaiaWsEventBus } from "@expert/gaia";
import { getLogger } from "@expert/logging";
import { addMessage, setupOrchestratorTimeout } from "@soluto-private/expert-workspace-timeline";

const logger = getLogger({ module: "GaiaTab" });

export const emitProactiveGaiaEvent = (
    partner: string,
    sessionId: string,
    suggestionOverrides: Record<string, unknown>,
) => {
    const timestamp = Date.now();
    gaiaWsEventBus.emit("gaia_ws_message-suggestion", {
        name: "message-suggestion",
        correlationId: `dev-tool-${timestamp}`,
        timestamp,
        data: undefined,
        body: {
            partner,
            sessionGroupId: sessionId,
            sessionId,
            suggestion: {
                message: "",
                messageId: `dev-${timestamp}`,
                messageType: "Proactive",
                producer: "orchestrator",
                outputTimestamp: new Date().toISOString(),
                ...suggestionOverrides,
            },
        },
    });
};

export const emitLocationCues = (
    partner: string,
    sessionId: string,
    cityVal: string,
    stateVal: string,
    setCity: (s: string) => void,
    setState: (s: string) => void,
) => {
    const cues = [
        { name: stateVal, type: "LOCATION_STATE" as const },
        { name: cityVal, type: "LOCATION_CITY" as const },
    ].filter((cue) => cue.name);
    if (cues.length === 0) return;
    emitProactiveGaiaEvent(partner, sessionId, {
        cues,
        metadata: { intentClassification: "location-detection", applicationIdentifier: "location-cues" },
    });
    setCity("");
    setState("");
};

export const emitDeviceCue = (
    partner: string,
    sessionId: string,
    device: string,
    setDeviceName: (s: string) => void,
) => {
    if (!device) return;
    emitProactiveGaiaEvent(partner, sessionId, { cues: [{ name: device, count: 1, type: "DEVICE" }] });
    setDeviceName("");
};

export const emitSalesChecklistBehavior = (partner: string, sessionId: string, behavior: SalesChecklistBehaviors) => {
    emitProactiveGaiaEvent(partner, sessionId, {
        responseType: "sales-checklist-mentions",
        sales_checklist_mentions: [{ name: behavior, value: true }],
    });
};

export const emitGuideRecommendation = (partner: string, sessionId: string, callCategory: CallCategory) => {
    emitProactiveGaiaEvent(partner, sessionId, {
        category: "proactive-notification",
        cues: [{ name: callCategory, type: "CALL_CATEGORY" }],
    });
};

export const simulateSilentTimeout = (sessionId: string) => {
    const correlationId = `dev-tool-timeout-${crypto.randomUUID()}`;
    addMessage(sessionId, {
        id: `dev-timeout-${crypto.randomUUID()}`,
        text: "[Dev Tool] Simulated expert message (no GAIA response will arrive)",
        type: "Expert",
        isUnread: false,
        status: "active",
    });
    setupOrchestratorTimeout(correlationId, sessionId, logger);
};

export const simulateOrchestratorError = (sessionId: string) => {
    const timestamp = Date.now();
    gaiaWsEventBus.emit("gaia_ws_orchestrator-invoke-error", {
        name: "orchestrator-invoke-error",
        correlationId: `dev-tool-${timestamp}`,
        timestamp,
        data: undefined,
        body: {
            message: "Failed to invoke orchestrator",
            status: 500,
            statusText: "Internal Server Error",
            trackingId: `dev-tool-tracking-${timestamp}`,
            responseText: "Internal server error",
            body: JSON.stringify({
                sessionId,
                sessionGroupId: sessionId,
                message: "Simulated expert message",
                userId: "dev-tool",
                messageId: `dev-${timestamp}`,
                messageType: "Search",
                userType: "expert",
            }),
        },
    });
};
